'use client';

import type { DropdownMenuContentProps } from '@magicschool/spellbook';
import { cn, DropdownMenu, IconButton, type IconButtonProps, useBreakpoint } from '@magicschool/spellbook';
import { type Language, languageLabels } from '@magicschool/supabase/types';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useLocale } from '@/hooks/useLocale';
import { analyticsTrack } from '@/util/amplitude/analyticsClient';

type LocalizationDropdownMenuProps = {
  analyticsEventName?: boolean;
  autoLocale?: Language | null;
  className?: string;
  sideOffset?: DropdownMenuContentProps['sideOffset'];
  variant?: IconButtonProps['variant'];
  color?: IconButtonProps['color'];
};

const LocalizationDropdownMenu = ({
  analyticsEventName,
  autoLocale,
  className,
  sideOffset,
  variant = 'outlined',
  color = 'neutral',
}: LocalizationDropdownMenuProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const { locale, onLocaleChange } = useLocale();
  const intl = useIntl();
  const title = intl.formatMessage({ id: 'translate-modifier' });
  const xsDown = useBreakpoint('xs', 'down');

  const handleLocaleChange = useCallback(
    async (newLocale: string) => {
      if (autoLocale === newLocale || locale === newLocale) return;

      await onLocaleChange(newLocale as Language);

      if (analyticsEventName) {
        const key = pathname.includes('signup') ? 'SignUp:Translate' : 'SignIn:Translate';
        analyticsTrack(key, { type: newLocale });
      }

      router.refresh();
    },
    [autoLocale, locale, onLocaleChange, pathname, analyticsEventName, router.refresh],
  );

  useEffect(() => {
    if (autoLocale && Object.keys(languageLabels).includes(autoLocale)) handleLocaleChange(autoLocale);
  }, [autoLocale, handleLocaleChange]);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenu.Tooltip delayDuration={150} mode="label">
        <DropdownMenu.Trigger asChild>
          <IconButton variant={variant} color={color} size={xsDown ? 'sm' : 'md'} name="languages" label={title} />
        </DropdownMenu.Trigger>
        <DropdownMenu.TooltipContent>{title}</DropdownMenu.TooltipContent>
      </DropdownMenu.Tooltip>
      <DropdownMenu.Content className={cn('w-72 max-h-64', className)} sideOffset={sideOffset}>
        <DropdownMenu.Header>{title}</DropdownMenu.Header>
        <DropdownMenu.RadioGroup id="demo-radio-group" onValueChange={handleLocaleChange} value={locale}>
          {Object.entries(languageLabels).map(([code, label]) => (
            <DropdownMenu.RadioItem key={code} value={code}>
              <DropdownMenu.ItemLabel>{label}</DropdownMenu.ItemLabel>
            </DropdownMenu.RadioItem>
          ))}
        </DropdownMenu.RadioGroup>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

export default LocalizationDropdownMenu;
